import { useMemo, useState } from 'react';
import { Truck } from 'lucide-react';
import {
  formatarReais,
  multiplicadorReputacao,
  reputacaoDoNivelML,
  ROTULO_REPUTACAO,
  TABELA_FRETE_ML,
  type Reputacao,
} from '@profitlens/calculo';
import { useCriterios } from '../servicos/configuracoes';
import type { StatusConexao } from '../servicos/mercadolivre';

/**
 * A tabela do Custo dos Envios, visível.
 *
 * Ela morava só no código, e dado que ninguém consegue olhar é dado que ninguém
 * consegue conferir — ainda mais este, que não vem de API e envelhece em
 * silêncio quando o Mercado Livre reajusta.
 *
 * Por isso a data de conferência fica no topo, grande, e não num rodapé.
 */
const dataBR = (iso: string) => iso.split('-').reverse().join('/');

const rotuloPeso = (limites: ReadonlyArray<number | null>, i: number): string => {
  const teto = limites[i];
  const piso = i === 0 ? 0 : limites[i - 1];
  const kg = (g: number) => (g / 1000).toLocaleString('pt-BR');
  if (teto === null || teto === undefined) return `mais de ${kg(piso as number)} kg`;
  if (i === 0) return `até ${kg(teto)} kg`;
  return `${kg(piso as number)} a ${kg(teto)} kg`;
};

const rotuloPreco = (limites: ReadonlyArray<number | null>, i: number): string => {
  const teto = limites[i];
  const piso = i === 0 ? 0 : (limites[i - 1] as number) + 1;
  if (teto === null || teto === undefined) return `${formatarReais(piso)} ou mais`;
  if (i === 0) return `até ${formatarReais(teto)}`;
  return `${formatarReais(piso)} a ${formatarReais(teto)}`;
};

export function AbaFrete({ conexao }: { conexao: StatusConexao | null }) {
  const { criterios } = useCriterios();
  const daConta = reputacaoDoNivelML(conexao?.reputacaoNivel);
  const [reputacao, setReputacao] = useState<Reputacao>('VERDE');

  const emUso = daConta ?? reputacao;
  const t = TABELA_FRETE_ML;

  /**
   * A tabela guardada é a VERDE. As outras saem por multiplicador, que muda na
   * faixa de R$ 79 — por isso o fator é calculado célula a célula, e não uma vez
   * só para a tabela inteira.
   */
  const linhas = useMemo(
    () =>
      t.valoresCents.map((linha, i) => ({
        peso: rotuloPeso(t.faixasPesoGramas, i),
        celulas: linha.map((v, j) => {
          const precoDaFaixa = (t.faixasPrecoCents[j] ?? 20_000) as number;
          const fator = multiplicadorReputacao(emUso, precoDaFaixa);
          return fator === null ? null : Math.round(v * fator);
        }),
      })),
    [emUso, t],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-c-res bg-c-res/10 px-4 py-3 text-sm text-tinta-2">
        <p>
          <strong className="text-tinta">Esta tabela não vem da API.</strong> O Mercado Livre não
          expõe o Custo dos Envios do vendedor — o endereço de cálculo devolve a cotação do
          comprador, que é outro número. Então ela é dado informado, com data.
        </p>
        <p className="mt-1.5">
          Vigente desde <strong className="text-tinta">{dataBR(t.vigenteDesde)}</strong>, conferida
          em <strong className="text-tinta">{dataBR(t.conferidaEm)}</strong>. Quando o Mercado Livre
          reajustar, <strong className="text-tinta">nada aqui vai avisar</strong> — é esta data que
          faz você desconfiar.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-regua-2 bg-superficie px-4 py-3">
        <span className="text-xs font-medium">Ver os valores para a reputação</span>
        {daConta ? (
          <span className="rounded bg-c-via/12 px-2 py-1 text-xs font-semibold text-c-via">
            {ROTULO_REPUTACAO[daConta]} — vinda da sua conta
          </span>
        ) : (
          <select
            value={reputacao}
            onChange={(e) => setReputacao(e.target.value as Reputacao)}
            className="rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          >
            {(['VERDE', 'AMARELA', 'LARANJA_VERMELHA'] as const).map((r) => (
              <option key={r} value={r}>
                {ROTULO_REPUTACAO[r]}
              </option>
            ))}
          </select>
        )}
        {criterios?.vendasMensaisEsperadas ? null : null}
      </div>

      <div className="overflow-x-auto rounded-md border border-regua-2">
        <table className="w-full min-w-[56rem] border-collapse bg-superficie text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 border-b border-regua-2 bg-superficie px-3 py-2 text-left font-mono text-[0.6rem] font-semibold tracking-wider text-marca uppercase">
                Peso considerado
              </th>
              {t.faixasPrecoCents.map((_, j) => (
                <th
                  key={j}
                  className="border-b border-regua-2 px-3 py-2 text-right font-mono text-[0.6rem] font-semibold tracking-wider text-marca uppercase"
                >
                  {rotuloPreco(t.faixasPrecoCents, j)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={l.peso} className="border-b border-regua last:border-0 even:bg-marca/4">
                <th
                  scope="row"
                  className={`sticky left-0 z-10 px-3 py-1.5 text-left text-xs font-medium ${
                    i % 2 === 1 ? 'bg-superficie-2' : 'bg-superficie'
                  }`}
                >
                  {l.peso}
                </th>
                {l.celulas.map((c, j) => (
                  <td key={j} className="num px-3 py-1.5 text-right text-xs">
                    {c === null ? (
                      <span
                        className="text-tinta-3 italic"
                        title="A fonte não informa o acréscimo desta reputação abaixo de R$ 79."
                      >
                        n/i
                      </span>
                    ) : (
                      formatarReais(c)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-1.5 text-xs text-tinta-3">
        <p className="flex items-start gap-1.5">
          <Truck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            <strong className="text-tinta-2">O peso que vale é o considerado</strong>: o maior entre
            o real e o cubado, sendo o cubado (comprimento × largura × altura em cm) ÷ 6.000. Um
            produto leve numa caixa grande paga pela caixa.
          </span>
        </p>
        <p>
          <strong className="text-tinta-2">Abaixo de R$ 19</strong>, o custo é limitado à metade do
          preço do produto — um item de R$ 10 paga no máximo R$ 5,00.
        </p>
        <p>
          <strong className="text-tinta-2">Não há coluna de destino</strong>: o vendedor paga o
          mesmo para qualquer canto do país. O que muda é peso, preço e reputação.
        </p>
        <p>
          Fonte: {t.fonte}.
        </p>
      </div>
    </div>
  );
}
